import { InputMaskField, useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    ViewModelForm,
    ViewModelServiceContext,
} from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { claimsMessages } from 'gw-capability-claim-react';
import { WizardPageTemplate } from 'gw-portals-wizard-components-ui';
import _ from 'lodash';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { withRouter } from 'react-router-dom';
import { GatewayFNOLService as FNOLService } from 'twia-capability-gateway-fnol';
import messages from '../../FNOL.messages';
import aiMessages from './AdditionalInformationPage.messages';
import Claim from '../../models/Claim';
import metadata from './AdditionalInformationPage.metadata.json5';
import styles from './AdditionalInformationPage.module.scss';

import { IconButton, Button, InputField, DropdownSelectField } from '@jutro/legacy/components';

const ROLES_TO_FILTER = ['friendneighborExt', "propertyaccessExt", "propertymanagerExt", "relativeExt", 'witness'];

const getPhoneFieldBasedOnPrimaryPhoneType = relatedContact => {
    let primaryPhoneType;

    switch (relatedContact.contact.primaryPhoneType) {
        case 'home':
            primaryPhoneType = 'homeNumber';
            break;
        case 'work':
            primaryPhoneType = 'workNumber';
            break;
        case 'mobile':
            primaryPhoneType = 'cellNumber';
            break;
        default:
            primaryPhoneType = 'homeNumber';
    }

    return primaryPhoneType;
};

function FNOLAdditionalInformationPage(props) {
    const { showAlert, showConfirm } = useModal();

    const { wizardData: claimVM, updateWizardData, authHeader,  history: { location: { state = {} }} } = props;
    const { ClaimDownloadService } = useDependencies('ClaimDownloadService');
    const { ClaimService } = useDependencies('ClaimService');
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const [claimObj, updateClaimObj] = useState({});
    const [showLoader, setShowLoader] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [tableValidation, setTableValidation] = useState(true);
    const [validateEmailValue, setValidateEmailValue] = useState(false);
    const { authUserData } = useAuthentication();
    const isTFPA = authUserData?.associationName_TWIA === 'tfpa';
    const isWindAndHail = ['bothExt', 'wind', 'hail'].includes(_.get(claimVM.value, 'lossCause'));
    const { onValidate, isComponentValid, registerComponentValidation } =
        useValidation('FNOLAdditionalInformationPage');
    const breakpoint = useContext(BreakpointTrackerContext);
    const isPhone = breakpoint === 'phoneWide' || breakpoint === 'phone';

    function Text() {
        return (<div> {'You may now upload any related documentation you would like to provide, such as photos of damages or repair estimates.'}&nbsp;
            {'Click the "Upload Documents" button, and select the files you wish to submit.'}
        </div>)
    }

    function uploadText() {
        return (<div>
            <p>After completing this report, you can access the claim under the Claims tab to submit additional documents.</p>
            <br></br>
            <p>Do not upload files containing illegal content or copyrighted information without the permission of the copyright owner.
                File names must not exceed 60 characters. Files must not exceed 20MB. Supported file types: .doc, .pdf, .jpg, etc. For a complete list of supported file types,
                click  <a href="https://www.twia.org/wp-content/uploads/2016/08/Claims-Center-File-Type-Restrictions.pdf" target="_blank" style={{ color: '#30A6DC', cursor: 'pointer', textDecorationLine: 'none' }}>here</a>.</p>
        </div>)
    }

    function contactText() {
        if (!isTFPA) {
            return (<div>
                <p><strong>You can provide information on any other person who may be involved with the claim.</strong></p>
                <br></br>
                <p>Adding other people does not grant TWIA permission to contact them directly. Policyholders will be contacted for permission to discuss details of TWIA policy and claims with anyone added.
                    You can remove a person for this claim by contacting us at (800) 788-8247.</p>
            </div>)
        }

        if (isTFPA) {
            return (<div>
                <p><strong>You can provide information on any other person who may be involved with the claim.</strong></p>
                <br></br>
                <p>Adding other people does not grant TFPA permission to contact them directly. Policyholders will be contacted for permission to discuss details of TFPA policy and claims with anyone added.
                    You can remove a person for this claim by contacting us at (800) 979-6440.</p>
            </div>)
        }


    }

    const showInjuredColumn = useMemo(
        () =>
            !['InlandMarine', 'GeneralLiability', 'BusinessOwners'].includes(
                _.get(claimVM, 'policy.policyType.value')
            ),
        // Policy type won't change on re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const checkTableValidation = useCallback(() => {
        const relatedContacts = claimVM.relatedContacts.value

        if (claimVM.relatedContacts.value.length > 0) {

            const result = relatedContacts.find((contact) => contact.contact.subtype === 'Person' && (
                contact.contact.firstName === undefined || contact.contact.lastName === undefined || contact.contact.homeNumber === undefined))

            if (_.isUndefined(result)) {
                setTableValidation(true)
            } else {
                setTableValidation(false)
            }

            if (showErrors) {
                setTableValidation(false)
            }

        } else {
            setTableValidation(true)
        }

    }, [claimVM, showErrors]);

    const writeValue = useCallback(
        (value, path) => {
            if (value === '') {
                _.set(claimVM, path, undefined);
            } else {
                _.set(claimVM, path, value);
            }

            updateWizardData(claimVM);
            _.set(claimObj, 'relatedContacts', claimVM.relatedContacts.value);
            checkTableValidation()
        },
        [checkTableValidation, claimObj, claimVM, updateWizardData]
    );

    const handleError = useCallback(
        (title, message) =>
            showAlert({
                title,
                message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop),
        [showAlert]
    );

    useEffect(() => {
        updateClaimObj(new Claim(claimVM.value));
        if (isTFPA) {
            const claimNumber = _.get(claimVM, 'claimNumber.value');
            const lossItemDetailsList = _.get(claimVM, 'lossDamagesExt.value[0]');
            if (!_.isEmpty(lossItemDetailsList && _.get(lossItemDetailsList, 'publicID')) && !isWindAndHail) {
                FNOLService.deleteClaimLossDamageExt(claimNumber, lossItemDetailsList, authHeader).then(() => { state.isLossDamageDeletedFromApi= true }).catch(() => {});
            }
        }
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFirstLastNameData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            const setDisable = item.contact.publicID ? 'disabled' : '';

            return (
                <InputField
                    id={`${id}_${rowId}`}
                    className={`${styles.tableColumn} ${isPhone ? styles.cellAlignMobile : ''}`}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    disabled={setDisable}
                    required
                    showRequired
                />
            );
        },
        [claimVM, writeValue]
    );

    const emailValidationsMessages = (value, item, emailVM) => {

        if (!_.isUndefined(value)) {
            if (!emailVM.aspects.valid) {
                item.contact.isEmailValid = false
                validateEmailValid()

                return [translator(messages.EmailValidationMessage)];
            }

            item.contact.isEmailValid = true
            validateEmailValid()
            return []

        }
        item.contact.isEmailValid = true
        validateEmailValid()
        return

    };

    const getEmailData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            const emailVMPath = `relatedContacts.children[${rowId}].${path}`;
            const setDisable = item.contact.publicID ? 'disabled' : '';

            return (
                <InputField
                    id={`${id}_${rowId}`}
                    className={`${styles.tableColumn} ${isPhone ? styles.cellAlignMobile : ''}`}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    disabled={setDisable}
                    validationMessages={emailValidationsMessages(_.get(claimVM, claimVMPath),item, _.get(claimVM, emailVMPath))}
                />
            );
        }, [claimVM, writeValue]
    )

    const filteredRoleTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO'
        );
        const filteredList = typeList.role.aspects.availableValues
            .filter(typeCode => ROLES_TO_FILTER.includes(typeCode.code))
            .map(typeCode => ({
                code: typeCode.code,
                name: translator({
                    id: typeCode.name,
                    defaultMessage: typeCode.name,
                }),
            }))
            .sort((a, b) => a.name.localeCompare(b.name)); // DE16504


        return filteredList
    }, [translator, viewModelService]);

    const getInvolvementInjuredData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            let availableValues;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;

            if (id === 'role') {
                availableValues = filteredRoleTypeList;

                console.log(availableValues)
            } else {
                availableValues = [
                    {
                        code: 'true',
                        name: translator(messages.fnolYes),
                    },
                    {
                        code: 'false',
                        name: translator(messages.fnolNo),
                    },
                ];
            }

            return (
                <DropdownSelectField
                    id={`${id}_${rowId}`}
                    alwaysShowPlaceholder={false}
                    availableValues={availableValues}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    className={`${styles.involvmentInjuredDropDown} ${isPhone ? styles.cellAlignMobile : ''}`}
                />
            );
        },
        [claimVM, filteredRoleTypeList, translator, writeValue]
    );

    const getPhoneNumberData = useCallback(
        (item, rowId) => {
            const phoneField = getPhoneFieldBasedOnPrimaryPhoneType(item);
            const claimVMPath = `relatedContacts.value[${rowId}].contact.${phoneField}`;
            const setDisable = item.contact.publicID ? 'disabled' : '';

            if (!_.isUndefined(item.contact.homeNumber)) {
                const phNumLength = item.contact.homeNumber.replace(/\D/g, '').length;

                if (phNumLength < 10) {
                    setTableValidation(false)
                }
            }

            return (
                <InputMaskField
                    alwaysShowMask
                    id={`${item.primaryPhoneID}_${rowId}`}
                    dataType="string"
                    mask="999-999-9999"
                    onValueChange={writeValue}
                    path={claimVMPath}
                    disabled={setDisable}
                    value={_.get(claimVM, claimVMPath)}
                    required
                    showRequired
                    className={isPhone ? styles.cellAlignMobile : ''}
                />
            );
        },
        [claimVM, writeValue]
    );

    // US26532 generate download token before downloading document
    const onDownloadDocument = useCallback(async (event, aDocument) => {
        event.stopPropagation();
        setShowLoader(true);

        try {
            const policyNumber = _.get(claimVM, 'policy.policyNumber.value');
            const docId = aDocument.docUID;
            const token = await ClaimService.claimsDocDownloadToken(
                [policyNumber, docId],
                authHeader
            );
            const downloadURL = ClaimDownloadService.getClaimDocument(
                docId,
                token
            );

            window.location.href = `${downloadURL}&claim=${claimVM.claimNumber.value}`;
        } catch (e) {
            console.log(`error downloading ${e}`);
            handleError(
                commonMessages.errorUploadTitle,
                aiMessages.downloadError
            );
        } finally {
            setShowLoader(false);
        }
    }, [claimVM, setShowLoader, ClaimService, ClaimDownloadService, handleError]);

    const generateMoreInfoDocumentsOverrides = useCallback(() => {
        const documentUploadPath = 'value.documents';
        const documentUploadItems = _.get(claimVM, documentUploadPath, []);
        const overrides = documentUploadItems.map(
            (documentUploadItem, index) => ({
                [`documentNameLink${index}`]: {
                    onClick: (event) => { onDownloadDocument(event, documentUploadItem) },
                    content: documentUploadItem.name,
                },
            })
        );

        return Object.assign({}, ...overrides);
    }, [
        claimVM,
        onDownloadDocument,
    ]);

    const getContactsRelatedPersons = useCallback(contactsItem => {
        if (contactsItem.subtype === 'Person' && contactsItem.lastName) {
            return `${contactsItem.firstName} ${contactsItem.lastName}`;
        }

        return '';
    }, []);

    const generateMoreInfoAddPersonOverrides = useCallback(() => {
        const contactsPath = 'value.contacts';
        const contactsItems = _.get(claimVM, contactsPath, []);
        let availableRelatedContacts = [];

        if (!_.isEmpty(claimObj)) {
            availableRelatedContacts = claimObj
                .availableRelatedContacts()
                .filter(
                    contact => contact.subtype === 'Person' && contact.firstName
                );
        }

        if (!contactsItems.length) {
            return {};
        }

        const overrides = contactsItems.map((contactItem, index) => ({
            [`contactItemsDropDown${index}`]: {
                content: getContactsRelatedPersons(contactItem),
                visible: _.includes(availableRelatedContacts, contactItem),
            },
        }));

        return Object.assign({}, ...overrides);
    }, [claimVM, claimObj, getContactsRelatedPersons]);

    const removeContactItem = useCallback(
        (e, rowId) => {
            e.preventDefault();

            const { relatedContacts } = claimVM;
            const relatedContactToDelete = relatedContacts.value.filter(
                (relatedContact, index) => index === rowId
            );

            claimObj.removeRelatedContact(relatedContactToDelete[0].contact);
            _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            checkTableValidation()
            updateWizardData(claimVM);
        },
        [claimVM, claimObj, updateWizardData]
    );

    const removeContactRow = useCallback(
        (item, index) => (
            <IconButton
                icon="mi-delete"
                onClick={e => removeContactItem(e, index, item)}
                className={isPhone ? styles.removeHeaderMobile : ''}
            />
        ),
        [removeContactItem]
    );

    const generateNewWitnessRow = useCallback(
        event => {
            event.preventDefault();

            const { contacts } = claimVM;

            claimObj.addRelatedContact(null);
           
            if(_.get(claimObj,'relatedContacts').length > 0) {
            for (const riskIdx in claimObj.relatedContacts) {
                const risk = claimObj.relatedContacts[riskIdx];
                claimObj.relatedContacts[riskIdx].contact.isEmailValid = true;
            }
        }
              
            _.set(
                claimVM.value,
                'relatedContacts',
                claimObj.relatedContacts
            );
            checkTableValidation();
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData, claimObj]
    );

    const validateEmailValid = useCallback(
        () => {
          const relatedContactsValue = _.get(claimVM.value, 'relatedContacts')

          if(relatedContactsValue.length > 0) {
            const found = relatedContactsValue.find((i) => i.contact.isEmailValid === false);
            
            if(!_.isUndefined(found)) {
                
                    setValidateEmailValue(true)
                } else {
                    setValidateEmailValue(false)
                
        }
       
        }
        }, []
    );

    const generateAddPersonMenuDropDown = useCallback(
        (dropDownProps, toggleMenu) => {
            const { isOpen, ref } = dropDownProps;
            const onAddPersonClick = () => {
                toggleMenu(!isOpen);
            };

            return (
                <Button
                    onClick={onAddPersonClick}
                    icon="mi-expand_more"
                    iconPosition="right"
                    ref={ref}
                >
                    {translator(messages.fnolAddPerson)}
                </Button>
            );
        },
        [translator]
    );

    const onUploadDocument = useCallback(
        async file => {

            setShowLoader(true);

            const documentMetaDataTemplate = {
                DocUID: '001',
                DocumentType: 'fnol',
                SecurityType: 'unrestricted',
                Status: 'approved',
                Author: 'Policy Holder',
                claimNumber: claimVM.claimNumber.value,
                name: file.name,
                mimeType: file.type,
                sessionID: "-"
            };

            try {
                const isProducer = authUserData?.userType === 'producer';
                _.set(documentMetaDataTemplate, 'isPEUser_ENT', isProducer);
                const data = await ClaimService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                const documentsArray = _.get(claimVM, 'documents.value');

                documentsArray.push(data);

                const newClaimVM = _.clone(claimVM);

                _.set(newClaimVM, 'documents.value', documentsArray);
                updateWizardData(newClaimVM);
                setShowLoader(false);
            } catch (error) {
                setShowLoader(false);
                handleError(
                    commonMessages.errorUploadTitle,
                    commonMessages.uploadFailedMessage
                );
            }
        },
        [
            claimVM,
            ClaimService,
            authHeader,
            updateWizardData,
            handleError,
        ]
    );

    const validateTableForm = useCallback(() => {
        if (
            !claimVM?.relatedContacts?.aspects?.valid ||
            !claimVM?.relatedContacts?.aspects?.subtreeValid
        ) {
            return false;
        }

        return true;
    }, [claimVM]);

    const headerfn = useCallback(() => {
        return <div><span style={{ color: "red" }}>*</span>First Name</div>
    })

    const headerlastName = useCallback(() => {
        return <div><span style={{ color: "red" }}>*</span>Last Name</div>
    })

    const headerPhoneNumber = useCallback(() => {
        return <div><span style={{ color: "red" }}>*</span>Phone Number</div>
    })

    const overrides = {
        '@field': {
            showRequired: true,
            showErrors
        },
        addtionalInformationLoader: {
            loaded: !showLoader,
        },
        uploadDocumentGrid: {
            visible: !showLoader,
        },
        witnessPartyTable: {
            data: claimVM?.relatedContacts?.value,
            visible: !_.isEmpty(claimVM?.relatedContacts?.value),
        },
        injuredHeader: {
            visible: showInjuredColumn,
        },
        claimInformation: {
            content: Text()
        },
        claimContainer: {
            content: uploadText()
        },
        contactContainer: {
            content: contactText()
        },
        firstNameHeader: {
            header: headerfn()
        },
        lastNameHeader: {
            header: headerlastName()
        },
        phoneNumberHeader: {
            header: headerPhoneNumber()
        },
        ...generateMoreInfoDocumentsOverrides(),
        ...generateMoreInfoAddPersonOverrides(),
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateWitnessPartyTableRow: generateNewWitnessRow,
            getFirstNameData: getFirstLastNameData,
            getLastNameData: getFirstLastNameData,
            getInvolvementData: getInvolvementInjuredData,
            getPhoneNumberData,
            getEmail: getEmailData,
            removeContactRow,
            onUploadDocument,
            onDownloadDocument,
            renderTrigger: generateAddPersonMenuDropDown,
        },
    };

    return (
        <WizardPage
            disableNext={!tableValidation || validateEmailValue}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLAdditionalInformationPage.propTypes = wizardProps;
export default withRouter(
    withAuthenticationContext(FNOLAdditionalInformationPage)
);
